import { apiClient } from '@/auth';

/**
 * Subset of Elastic V2 match fields we actually consume. V2 returns
 * `food_name` / `food_name_translations`; we normalize to `name` /
 * `name_translations` so meal-item UI can keep using `tAttr(row, 'name')`.
 *
 * Canonical `food_name` is always what we put in `name` (and therefore what
 * gets saved on pick). Patient aliases are kept separately for display only,
 * matching mobile's selectMatchAsMealItem behavior.
 */
/**
 * One serving option for a food (from `serving_units` on the search result,
 * FoodServingUnit @ swagger.yaml:17629). `label` is the unit name
 * (gram / cup / slice…); `amount` is that unit's weight — grams, occasionally
 * mL — i.e. the value that lands in a meal item's `serving_unit_amount`.
 */
export type FoodServingUnit = {
  label: string;
  amount: number;
  default_label_qty?: number;
  ranking?: number;
  addon?: boolean;
  /** Preset serving quantities offered for this unit — surfaced as
   *  quick-pick suggestions on the Servings field. */
  food_sizing_options?: string[];
};

export type FoodSearchRow = {
  name: string;
  name_translations?: Record<string, string> | null;
  /** Patient-specific alias from the personal index - display only; never
   *  submitted as meal-item `food_name`. */
  food_name_alias?: string | null;
  food_name_alias_translations?: Record<string, string> | null;
  base_name?: string;
  /** Serving options for this food — used to populate the meal-item / add-on
   *  editor's Unit dropdown (and auto-fill the matching gram amount). */
  serving_units?: FoodServingUnit[] | null;
};

/** Wire shape of `GET /foods/search/v2/interactive` (FoodSearchV2Response). */
export type FoodSearchV2Match = {
  id: string;
  food_name: string;
  food_name_translations?: Record<string, string> | null;
  food_name_alias?: string | null;
  food_name_alias_translations?: Record<string, string> | null;
  serving_units?: FoodServingUnit[] | null;
};

type FoodSearchV2Response = {
  count: number;
  matches: FoodSearchV2Match[];
  suggestions?: unknown[];
};

export type FoodSearchContext = {
  contextUserId?: number;
  contextLang?: string;
  contextMealName?: string | null;
  contextAddonOf?: string | null;
};

/** Map a V2 match to the portal row shape. Exported for unit tests. */
export const matchToRow = (m: FoodSearchV2Match): FoodSearchRow | null => {
  const name = m.food_name?.trim() ?? '';
  if (!name) return null;
  const alias = m.food_name_alias?.trim() || null;
  return {
    name,
    name_translations: m.food_name_translations ?? null,
    food_name_alias: alias,
    food_name_alias_translations: alias ? (m.food_name_alias_translations ?? null) : null,
    serving_units: Array.isArray(m.serving_units) ? m.serving_units : null,
  };
};

export const foodsApi = {
  /**
   * GET /foods/search/v2/interactive - Elastic V2 interactive search (same
   * endpoint mobile typeahead uses). Replaces Azure `/foods/search_by_text`
   * (FOOD-862). Up to 20 matches per call.
   *
   * `context_match_strategy` is `standard` (mobile's non-flag path). Hybrid
   * stays behind mobile's `elasticHybridSearch` app flag; clinician apps
   * have no equivalent flag yet.
   *
   * Treat 403/404 as empty so the autocomplete dropdown stays quiet for
   * accounts that lack food-search permission entirely.
   */
  async autocomplete(
    partialTerm: string | undefined,
    context: FoodSearchContext = {},
  ): Promise<FoodSearchRow[]> {
    const term = partialTerm ?? '';
    if (!term.trim() || term.trim().length < 2) return [];
    try {
      const res = await apiClient.get<FoodSearchV2Response>(`/foods/search/v2/interactive`, {
        params: {
          q: term,
          context_user_id: context.contextUserId,
          context_lang: context.contextLang || undefined,
          context_meal_name: context.contextMealName || undefined,
          context_addon_of: context.contextAddonOf || undefined,
          context_match_strategy: 'standard',
        },
      });
      const matches = Array.isArray(res.data?.matches) ? res.data.matches : [];
      return matches.map(matchToRow).filter((r): r is FoodSearchRow => r != null);
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 403 || status === 404) return [];
      throw err;
    }
  },
};
